import * as React from "react"
import { Container, Hidden, Row, Visible } from "react-grid-system"
import styled, { css } from "styled-components"
import { theme } from "../../components/theme"
import { Link, useLocation } from "react-router-dom"
import { SatcubeLogo } from "../../images/SatcubeLogo"
import { MediaQuery } from "../../utils/design-helpers"
import MobileNavBar from "../molecules/HomePageComponents/MobileNavBar"
import { AnimatePresence } from "framer-motion"
import Modal from "../molecules/Modals/Modal"
import LoginModalContainer from "./LoginScreens/LoginModalContainer"
import { Spacer } from "../atoms/spacers"
import { AccountCircleIcon } from "../atoms/icons/AccountCircleIcon"
import { ShoppingCartIcon } from "../atoms/icons/ShoppingCartIcon"
import { CrossIcon } from "../atoms/icons/CrossIcon"
import { HamburgerMenuIcon } from "../atoms/icons/HamburgerMenu"

import { LogoutIcon } from "../atoms/icons/LogoutIcon"
import Tooltip, { TooltipContainer, TooltipItem } from "../molecules/Tooltip"
import { useScrollY } from "../../hooks/useScroll"
import { NavItem, navItemsDesktop, navItemsDesktopLoggedIn } from "../../utils/nav-helpers"
import { useIsSignedIn } from "../../hooks/useIsSignedIn"
import { CartStructure, Product } from "../interfaces/SaleorTypes"
import { useClickOutside } from "../../hooks/useClickOutside"
import { RequestDemoButton } from "../molecules/store/RequestDemo"
import { CartSolutionOrder } from "../../utils/models"
import Body from "../atoms/typography/Body"
import ButtonTextSmall from "../atoms/typography/ButtonTextSmall"
import { ArrowRight } from "../atoms/icons/ArrowRight"
import Cart from "../templates/store/Cart"
import H3 from "../atoms/typography/H3"
import { useUser } from "../../hooks/useUser"
import SmallButton from "../atoms/buttons/SmallButton"

interface HeaderProps {
  cartNumber?: number
  cart: CartStructure[]
  removeFromCart: (products: CartStructure[]) => void
  addToCart: (products: CartStructure[]) => void
  updateItemInCart: (item: Product) => void
  clearCart: () => void
  solutions: CartSolutionOrder[]
  removeSolution: (id: string) => void
  cartOpen: boolean
  setCartOpen: any
}

interface HeaderContainerProps {
  transparent?: boolean
  fixed?: boolean
}

const ProfileImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
`

const HeaderWrapper = styled.div<HeaderContainerProps>`
  max-width: 100vw;
  padding-block: ${({ theme }) => theme.spacings.large};
  padding-right: 60px;
  padding-left: 60px;

  position: sticky;
  z-index: 5;
  top: 0;

  ${({ fixed }) =>
    fixed &&
    css`
      position: fixed;
      width: 100vw;

      transition-property: background-color;
      transition-duration: 250ms;
      transition-timing-function: ease-in;
    `}

  background-color: ${({ transparent }) => `rgb(26, 26, 26, ${transparent ? 0 : 1})`};

  ${MediaQuery.XS} {
    padding: 0.75rem;
  }
`

const LogoButton = styled.div`
  cursor: pointer;
`

const IconButton = styled.div<{ active?: boolean }>`
  cursor: pointer;
  ${(props) => props.active && "filter: brightness(60%);"}
  position: relative;

  :hover {
    filter: brightness(60%);
  }
`

const CartNumberContainer = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -5px;
  right: -5px;
`

const HeaderNavigationButton = styled.div<{ active: boolean }>`
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: ${({ active }) => (active ? "700" : "400")};
  font-style: ${({ active }) => (active ? "bold" : "normal")};
  background: ${(props) => (props.active ? theme.gradients.yellow : props.theme.colors.white)};
  background-clip: text;

  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

interface CartIconProps {
  cartNumber?: number
}

export const CartIcon: React.FC<CartIconProps> = ({ cartNumber }) => {
  return (
    <div style={{ position: "relative" }}>
      {cartNumber !== undefined && cartNumber > 0 && (
        <CartNumberContainer>{<Body>{cartNumber}</Body>}</CartNumberContainer>
      )}
      <ShoppingCartIcon />
    </div>
  )
}

const Header: React.FunctionComponent<HeaderProps> = ({
  cartNumber,
  addToCart,
  cart,
  removeFromCart,
  updateItemInCart,
  clearCart,
  solutions,
  removeSolution,
  cartOpen,
  setCartOpen,
}) => {
  const location = useLocation()
  const { isSignedIn } = useIsSignedIn()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [loginModalOpen, setLoginModalOpen] = React.useState(false)
  const scroll = useScrollY()
  const isHomePage = React.useMemo(() => window.location.pathname == "/", [window.location.pathname])
  const user = useUser()

  const items = navItemsDesktop()
  const itemsLoggedIn = navItemsDesktopLoggedIn(isSignedIn)

  const intersperseSpacers = (items: JSX.Element[]) =>
    items.flatMap((item, i) => [<Spacer key={i} spacing={2} />, item]).slice(1)

  const renderNavItems = (items: NavItem[]) =>
    items
      .map(
        (item) =>
          !item.hidden && (
            <Link to={item.path} key={item.title} id={`navlink-${item.title}`}>
              <HeaderNavigationButton active={location?.pathname.includes(item.path)}>
                <H3>{item.title}</H3>
              </HeaderNavigationButton>
            </Link>
          ),
      )
      .filter((item): item is JSX.Element => item !== false)

  const mobileNavRef = useClickOutside<HTMLDivElement>(() => setMenuOpen(false))

  const profileButton = (
    <Link to="/profile">
      <IconButton id="userProfile">
        {user.loading ? <AccountCircleIcon /> : <ProfileImage src={user.user?.profile?.imageUrl} />}
      </IconButton>
    </Link>
  )

  const loginButton = (
    <div onClick={() => setLoginModalOpen(!loginModalOpen)} id="userProfile">
      <SmallButton variant="light_transparent" title="Login" />
    </div>
  )

  return (
    <HeaderWrapper fixed={isHomePage} transparent={!menuOpen && isHomePage && scroll < 50}>
      <Container>
        <Row justify="between" align="center" nogutter>
          <Row nogutter>
            <Link to="/" id="homepage">
              <LogoButton>
                <SatcubeLogo />
              </LogoButton>
            </Link>
          </Row>

          <Hidden xs sm md>
            <Row nogutter>
              {intersperseSpacers(renderNavItems(items))}
              {isSignedIn && (
                <>
                  <Spacer spacing={1} />
                  <H3 color={theme.colors.white}>|</H3>
                  <Spacer spacing={1} />
                  {intersperseSpacers(renderNavItems(itemsLoggedIn))}
                </>
              )}
            </Row>

            <Row align="center" nogutter>
              <RequestDemoButton productType={"satcube_ku"} buttonType="small" />
              <Spacer spacing={2} />
              <IconButton onClick={() => setCartOpen(!cartOpen)} id="userCart">
                <CartIcon cartNumber={cartNumber} />
              </IconButton>
              <Spacer spacing={2} />
              {isSignedIn ? (
                <TooltipContainer>
                  {profileButton}
                  <Tooltip>
                    <TooltipItem as={Link} to="/profile">
                      <ButtonTextSmall>Go to my profile</ButtonTextSmall>
                      <ArrowRight color={theme.colors.greyDark} />
                    </TooltipItem>
                    <TooltipItem as="a" href="/logout">
                      <ButtonTextSmall color={theme.colors.red}>Log out</ButtonTextSmall>
                      <LogoutIcon fill={theme.colors.red} />
                    </TooltipItem>
                  </Tooltip>
                </TooltipContainer>
              ) : (
                <>{loginButton}</>
              )}
            </Row>
          </Hidden>

          <Visible xs sm md>
            <div ref={mobileNavRef}>
              <Row align="center">
                <IconButton onClick={() => setMenuOpen(!menuOpen)}>
                  {menuOpen ? <CrossIcon color="white" /> : <HamburgerMenuIcon color="white" />}
                </IconButton>
                <Spacer spacing={1} />
                {isSignedIn ? profileButton : loginButton}
              </Row>
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  left: 0,
                  bottom: 0,
                }}
              >
                <MobileNavBar
                  open={menuOpen}
                  userLoggedIn={isSignedIn}
                  cartNumber={cartNumber}
                  onClose={() => setMenuOpen(false)}
                />
              </div>
            </div>
          </Visible>
        </Row>
      </Container>

      <Cart
        open={cartOpen}
        addToCart={addToCart}
        cart={cart}
        removeFromCart={removeFromCart}
        updateItemInCart={updateItemInCart}
        clearCart={clearCart}
        onClose={() => setCartOpen(false)}
        solutions={solutions}
        removeSolution={removeSolution}
      />
      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {loginModalOpen && (
          <Modal
            open={loginModalOpen}
            onClose={() => setLoginModalOpen(!loginModalOpen)}
            backdropColor="#D5E2EAe1"
            closeIcon
          >
            <LoginModalContainer onCloseModal={() => setLoginModalOpen(false)} />
          </Modal>
        )}
      </AnimatePresence>
    </HeaderWrapper>
  )
}

export default Header
